import React from "react";
import { ArrowRight, ShieldCheck, Phone, ClipboardCheck, CalendarCheck } from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/31197870/pexels-photo-31197870.jpeg";

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const requestAudit = () => {
    scrollTo("contact");
    setTimeout(() => {
      const sel = document.querySelector('[data-testid="contact-input-service"]');
      if (sel) {
        sel.value = "Risk & Compliance / ISO Audit";
        sel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      const msg = document.querySelector('[data-testid="contact-input-message"]');
      if (msg && !msg.value) {
        const native = Object.getOwnPropertyDescriptor(
          window.HTMLTextAreaElement.prototype, "value"
        ).set;
        native.call(msg, "I would like to request an EHS audit / workplace safety assessment for our site. Please get in touch with scope and next steps.");
        msg.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }, 700);
  };

  return (
    <section
      data-testid="hero"
      className="dark-section relative overflow-hidden bg-bgdark text-white"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div aria-hidden className="absolute inset-0 bg-black/72"></div>
      <div aria-hidden className="absolute inset-0 grain pointer-events-none"></div>

      {/* Top strip */}
      <div className="relative border-b border-white/15">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.2em] text-white/60">
          <span>
            M/s Sabari Consultant <span className="text-accent-amber">/</span>{" "}
            Consult · Train · Protect
          </span>
          <span className="hidden sm:block">
            Bengaluru · Karnataka · India
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-accent-amber animate-pulse"></span>
            On-Site &amp; Remote Advisory
          </span>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-28 lg:pb-40">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="mono-label flex items-center gap-3">
              <span className="w-8 h-px bg-accent-amber inline-block"></span>
              Environment · Health · Safety · SAP EHS
            </p>

            <h1
              data-testid="hero-heading"
              className="font-display mt-6 text-[36px] leading-[1.02] sm:text-5xl lg:text-[72px] xl:text-[84px] uppercase tracking-tighter"
            >
              Practical EHS Consulting <br className="hidden lg:block" />
              &amp; <span className="text-accent-amber">SAP EHS Support</span>
              <br />
              for safer, compliant <br className="hidden lg:block" />
              workplaces<span className="cursor-blink"></span>
            </h1>

            <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
              Sabari Consultant partners with manufacturing, construction,
              warehousing and IT-campus operations to design, implement and
              audit workplace safety, compliance and digital EHS systems. Led
              by Sabari Giri Eshwaran —{" "}
              <span className="text-white font-medium">17+ years of practice</span>,{" "}
              <span className="text-white font-medium">35M safe man-hours</span> and{" "}
              <span className="text-accent-amber font-medium">zero LTI</span>.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                data-testid="hero-cta-book-consultation"
                onClick={() => scrollTo("contact")}
                className="btn-primary"
              >
                <CalendarCheck size={16} /> Book a Consultation
              </button>
              <button
                data-testid="hero-cta-request-audit"
                onClick={requestAudit}
                className="btn-ghost text-white border-white/30 hover:bg-white hover:text-bgdark"
              >
                <ClipboardCheck size={16} /> Request an EHS Audit
              </button>
              <a
                data-testid="hero-cta-talk-to-sabari"
                href="tel:+919738509782"
                className="btn-ghost text-accent-amber border-accent-amber/40 hover:bg-accent-amber hover:text-bgdark"
              >
                <Phone size={16} /> Talk to Sabari
                <ArrowRight size={14} className="ml-1" />
              </a>
            </div>

            {/* SEO trust strip */}
            <div
              data-testid="hero-trust-strip"
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55"
            >
              <span>EHS Consultant in Bengaluru</span>
              <span className="text-white/30">·</span>
              <span>Safety Audit Consultant</span>
              <span className="text-white/30">·</span>
              <span>SAP EHS Consultant</span>
              <span className="text-white/30">·</span>
              <span>Risk Assessment</span>
            </div>
          </div>

          {/* Right meta block */}
          <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/15 flex flex-col justify-between gap-8">
            <div>
              <p className="mono-label">Founder</p>
              <p className="font-display text-2xl mt-2 uppercase tracking-tight">
                Sabari Giri
                <br />
                Eshwaran
              </p>
              <p className="text-sm text-white/60 mt-2">
                EHS Consultant · HOD — EHS · CPD Safety Professional · Lead Auditor ISO 45001
              </p>
            </div>
            <div className="border-t border-white/15 pt-6">
              <p className="mono-label">Standards</p>
              <p className="font-mono text-sm mt-3 text-white/80 leading-relaxed">
                ISO 45001 · ISO 14001 · ISO 9001
                <br />
                NEBOSH IGC · IOSH · SAP S/4HANA EHS
              </p>
            </div>
            <div className="flex items-center gap-3 text-white/70 text-sm">
              <ShieldCheck size={18} className="text-accent-amber" />
              Zero non-compliances across 20 consecutive ISO audits
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
